import { useCallback, useEffect, useMemo, useState } from "react";
import adminService from "../../api/services/adminService";
import { buildBoostHistoryParams } from "./buildParams";
import { getFilterLabel as getFilterLabelUtil } from "./getFilterLabel";
import { INITIAL_FILTERS, LIMIT } from "./constants";

export function useBoostHistory() {
  const [historyFilters, setHistoryFilters] = useState(INITIAL_FILTERS);
  const [currentPage, setCurrentPage] = useState(1);
  const [boostHistory, setBoostHistory] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [historyError, setHistoryError] = useState("");

  const fetchBoostHistory = useCallback(async (page = 1, filters = {}) => {
    setLoadingHistory(true);
    setHistoryError("");
    try {
      const params = buildBoostHistoryParams(filters, page, LIMIT);
      const response = await adminService.getBoostHistory(params);
      if (response?.success && response?.history) {
        setBoostHistory(response.history);
        if (response.pagination) {
          setCurrentPage(response.pagination.currentPage);
          setTotalPages(response.pagination.totalPages);
          setTotalItems(response.pagination.totalItems);
        }
      }
    } catch (err) {
      console.error("Error fetching boost history:", err);
      setHistoryError(err?.message || "Failed to fetch boost history");
    } finally {
      setLoadingHistory(false);
    }
  }, []);

  useEffect(() => {
    fetchBoostHistory(currentPage, historyFilters);
  }, [
    currentPage,
    historyFilters.user_id,
    historyFilters.action,
    historyFilters.start_date,
    historyFilters.end_date,
    historyFilters.search,
    fetchBoostHistory,
  ]);

  const handleFilterChange = useCallback((key, value) => {
    setHistoryFilters((prev) => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  }, []);

  const clearFilters = useCallback(() => {
    setHistoryFilters(INITIAL_FILTERS);
    setCurrentPage(1);
  }, []);

  const removeFilter = useCallback((key) => {
    setHistoryFilters((prev) => ({ ...prev, [key]: "" }));
    setCurrentPage(1);
  }, []);

  const getFilterLabel = useCallback((key, value) => getFilterLabelUtil(key, value), []);

  const getActiveFiltersCount = useCallback(() => {
    return Object.values(historyFilters).filter(
      (v) => v !== "" && v != null
    ).length;
  }, [historyFilters]);

  const activeFilterEntries = useMemo(
    () =>
      Object.keys(historyFilters).filter(
        (key) => historyFilters[key] !== "" && historyFilters[key] != null
      ),
    [historyFilters]
  );

  const primaryFields = useMemo(
    () => [
      {
        name: "search",
        label: "Search",
        type: "text",
        placeholder: "Search users...",
      },
      {
        name: "user_id",
        label: "User ID",
        type: "number",
        placeholder: "Filter by user ID",
      },
      {
        name: "action",
        label: "Action",
        type: "select",
        options: [
          { value: "", name: "All Actions" },
          { value: "purchased", name: "Purchased" },
          { value: "extended", name: "Extended" },
          { value: "admin_granted", name: "Admin granted" },
          { value: "cancelled", name: "Cancelled" },
          { value: "expired", name: "Expired" },
        ],
      },
      { name: "start_date", label: "Start Date", type: "date" },
      { name: "end_date", label: "End Date", type: "date" },
    ],
    []
  );

  return {
    historyFilters,
    setHistoryFilters,
    handleFilterChange,
    clearFilters,
    removeFilter,
    getFilterLabel,
    getActiveFiltersCount,
    activeFilterEntries,
    primaryFields,
    boostHistory,
    currentPage,
    setCurrentPage,
    totalPages,
    totalItems,
    loadingHistory,
    historyError,
    limit: LIMIT,
    fetchBoostHistory,
  };
}
